import { Add, Delete, WarningAmber } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { Employee } from '../../../types/Employee';
import { ConflictEmployeeEvent, EventEmployee } from '../../../types/Event';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../../AccordionSection';
import AddEmployeeModal from './AddEmployeeModal';
import ConflictPopup, { ConflictState } from './ConflictPopover';
import DrawerLink from '../../common/drawers/ModuleDrawer/DrawerLink';
import { Preference } from '../../../types/Preference';

interface IEmployeeTableProps {
  eventEmployees: EventEmployee[];
  setEventEmployees: (eventEmployees: EventEmployee[]) => void;
}

export default function EmployeeTable({ eventEmployees, setEventEmployees }: IEmployeeTableProps) {
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [conflictState, setConflictState] = useState<ConflictState>({
    anchorEl: null,
    conflicts: [],
  });

  const { disabled } = useContext(DisableContext);

  // Add employees
  const onAdd = (addedEmployees: Employee[], employeeRole?: Preference | null) => {
    const netNew = addedEmployees.filter((emp) => !eventEmployees.some((ee) => ee.employee.uuid === emp.uuid && ee.employee_role_uuid === employeeRole?.uuid));

    const newEventEmployees = [
      ...eventEmployees,
      ...netNew.map((emp) => ({
        employee: emp,
        conflict_employee_events: [],
        employee_role_uuid: employeeRole?.uuid,
        employee_role_value: employeeRole?.value,
      })),
    ];

    setEventEmployees(newEventEmployees);
  };

  const getEventEmployeeKey = (ee: EventEmployee) => {
    return `${ee.employee.uuid}-${ee.employee_role_uuid ?? ''}`;
  };

  // Remove employees
  const handleRemove = () => {
    const filtered = _.reject(eventEmployees, (ee) => _.includes(selectedEmployees, getEventEmployeeKey(ee)));
    setEventEmployees(filtered);
    setSelectedEmployees([]);
  };

  const openConflict = (e: React.MouseEvent<HTMLElement>, conflicts: ConflictEmployeeEvent[]) => {
    e.stopPropagation();
    setConflictState({
      anchorEl: e.currentTarget,
      conflicts,
    });
  };

  const closeConflict = () => {
    setConflictState({
      anchorEl: null,
      conflicts: [],
    });
  };

  const employeeTableRows = _.map(eventEmployees, (ee) => {
    const employee = ee.employee;
    const conflicts = ee.conflict_employee_events || [];
    const rowKey = getEventEmployeeKey(ee);

    const handleSelectionChange = (checked: boolean) => {
      const nextSelections = checked ? [...selectedEmployees, rowKey] : _.reject(selectedEmployees, (id) => id === rowKey);

      setSelectedEmployees(nextSelections);
    };

    return (
      <TableRow key={getEventEmployeeKey(ee)} sx={{ ...listPageTableRowStyling }}>
        <TableCell>
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={_.includes(selectedEmployees, getEventEmployeeKey(ee))}
            onChange={(event) => handleSelectionChange(event.target.checked)}
          />
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!!conflicts.length && (
              <Tooltip title="Conflicting events">
                <IconButton size="small" onClick={(e) => openConflict(e, conflicts)} sx={{ color: '#f39c12' }}>
                  <WarningAmber fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <DrawerLink module={UserConfigurableModule.EMPLOYEES} uuid={employee.uuid}>
              {employee.display_name}
            </DrawerLink>
          </Box>
        </TableCell>

        <TableCell>{ee.employee_role_value}</TableCell>
        <TableCell>{employee.email}</TableCell>
        <TableCell>{employee.phone}</TableCell>
      </TableRow>
    );
  });

  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'end' }}>
      {eventEmployees.length > 0 && (
        <ResponsiveButton startIcon={<Delete />} variant="outlined" color="warning" onClick={handleRemove} disabled={disabled}>
          Remove
        </ResponsiveButton>
      )}

      <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setModalOpen(true)} disabled={disabled}>
        Add
      </ResponsiveButton>
    </Box>
  );

  return (
    <AccordionSection
      title="Employees Assigned"
      headerElements={actionButtons}
      collapseId={UserSettingsCollapseKeys.EVENT_ITEM_EMPLOYEES}
      hasData={eventEmployees.length > 0}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>{employeeTableRows}</TableBody>
        </Table>
      </TableContainer>

      <ConflictPopup conflictState={conflictState} onClose={closeConflict} />

      <AddEmployeeModal open={modalOpen} onClose={() => setModalOpen(false)} alreadyAddedEmployees={eventEmployees} onAdd={onAdd} />
    </AccordionSection>
  );
}
